'use client';

import { useInstantSearch } from 'react-instantsearch';
import { NoResults } from './NoResults';
import { ReactNode, useMemo } from 'react';

interface NoResultsBoundaryProps {
  children?: ReactNode;
  fallback?: ReactNode;
  clientEmpty?: boolean;
}

export function NoResultsBoundary({ children, fallback = <NoResults /> }: NoResultsBoundaryProps) {
  const { results } = useInstantSearch();

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned.
  if (!results.__isArtificial && results.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children ?? null}</div>
      </>
    );
  }

  return children ?? null;
}

interface NoScopedResultsBoundaryProps {
  children?: ReactNode;
  fallback?: ReactNode;
  /**
   * List of indices name to check for scoped results
   */
  scopedIndices?: string[];
  /**
   * Mode to determine how to evaluate results across scoped indices
   * - 'any': Show results if any of the scoped indices have results
   * - 'all': Show results only if all of the scoped indices have results
   */
  mode?: 'any' | 'all';
}

/**
 * No Scoped Results Boundary.
 * If scopedIndices is provided, checks results only for those indices.
 * Uses 'any' or 'all' mode to determine if results exist
 */
export function NoScopedResultsBoundary({
  children,
  fallback = <NoResults />,
  scopedIndices,
  mode = 'any',
}: NoScopedResultsBoundaryProps) {
  const { scopedResults } = useInstantSearch();

  const { totalNbHits, __isArtificial } = useMemo(() => {
    // Filter results based on scopedIndices if provided
    const filteredResults = scopedIndices
      ? scopedResults.filter(({ results }) => results && scopedIndices.includes(results.index))
      : scopedResults;

    // Calculate total hits from filtered results
    const totalNbHits = filteredResults.reduce((acc, { results }) => acc + (results?.nbHits ?? 0), 0);

    // Check if results are artificial
    const __isArtificial = filteredResults.some(({ results }) => results?.__isArtificial);

    // If mode is 'all', check that all specified indices have results
    if (mode === 'all') {
      const indicesWithResults = filteredResults.filter(({ results }) => results && results.nbHits > 0);
      const allIndicesHaveResults = indicesWithResults.length === filteredResults.length;

      // If not all indices have results, treat as no results
      if (!allIndicesHaveResults) {
        return { totalNbHits: 0, __isArtificial: false };
      }
    }

    return { totalNbHits, __isArtificial };
  }, [scopedResults, scopedIndices, mode]);

  // The `__isArtificial` flag makes sure not to display the No Results message
  // when no hits have been returned.
  if (!__isArtificial && totalNbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children ?? null}</div>
      </>
    );
  }

  return children ?? null;
}
